/**
 * Sentry `beforeBreadcrumb` hook: keeps console breadcrumbs without their raw
 * arguments.
 *
 * The console breadcrumb built by `breadcrumbsIntegration` carries
 *   `data.arguments` — the actual argument list handed to `console.error` — in
 *   addition to the joined `message`. Session Replay records the same
 *   breadcrumbs. So every object logged anywhere in the app leaves the device by
 *   default, and nothing in `Sentry.init` filtered that before this.
 *
 * Call sites are expected to log summaries rather than raw payloads (see
 *   `summariseErrorBody`), and this is the backstop for the ones that forget, or
 *   for a dependency logging on our behalf. The breadcrumb itself is kept —
 *   knowing *that* `send_form failed` fired, and in what order, is most of its
 *   value — and only the argument list is dropped.
 *
 * Deliberately not a denylist of field names: the bodies worth worrying about
 *   are the ones we did not author, so their field names are not knowable here.
 */
import type { Breadcrumb, BreadcrumbHint } from '@sentry/react';

export const scrubBreadcrumb = (
  breadcrumb: Breadcrumb,
  _hint?: BreadcrumbHint,
): Breadcrumb | null => {
  if (breadcrumb.category !== 'console') return breadcrumb;

  const { arguments: droppedArguments, ...rest } = breadcrumb.data ?? {};

  return {
    ...breadcrumb,
    data: {
      ...rest,
      ...(droppedArguments === undefined
        ? {}
        : { argumentsDropped: Array.isArray(droppedArguments) }),
    },
  };
};
